import {
  DragDropContext,
  Draggable,
  Droppable,
  type DraggableProvided,
  type DropResult,
  type DroppableProvided,
} from '@hello-pangea/dnd'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useCallback, useRef, useState } from 'react'
import { CARD_SLOT_HEIGHT } from '../cards/CardPreview.js'
import styles from './DragVirtualPrototype.module.css'

/**
 * Standalone proof of the virtual-droppable combination, kept in the app so it can be
 * re-run whenever either library is upgraded. It covers the cases the full board relies
 * on: a 500-item list, an empty destination, moving between lists, keyboard dragging,
 * and scrolling far enough that the source item is unmounted mid-drag.
 *
 * Scroll ownership: each list's scroller is the droppable AND the virtualizer's scroll
 * element, and the only scrollable ancestor is the horizontal track — the one level of
 * nesting the drag library supports.
 */
const LISTS = ['dense', 'small', 'empty'] as const
type ListId = (typeof LISTS)[number]

function initialItems(): Record<ListId, string[]> {
  return {
    dense: Array.from({ length: 500 }, (_, index) => `dense-${index + 1}`),
    small: ['small-1', 'small-2', 'small-3'],
    empty: [],
  }
}

export function DragVirtualPrototype() {
  const [items, setItems] = useState(initialItems)

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result
    if (!destination) return
    setItems((current) => {
      const from = source.droppableId as ListId
      const to = destination.droppableId as ListId
      const next = { ...current, [from]: [...current[from]] }
      const [moved] = next[from].splice(source.index, 1)
      if (moved === undefined) return current
      if (from !== to) next[to] = [...current[to]]
      next[to].splice(destination.index, 0, moved)
      return next
    })
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>Virtual drag-and-drop prototype</h1>
        <p>
          Drag with the pointer, or focus an item and press Space, then the arrow keys, then Space again. The mounted
          count never approaches the item count — virtualization stays on throughout the drag.
        </p>
      </header>
      <DragDropContext onDragEnd={onDragEnd}>
        <div className={styles.track} data-testid="prototype-track">
          {LISTS.map((listId) => (
            <VirtualList key={listId} listId={listId} items={items[listId]} />
          ))}
        </div>
      </DragDropContext>
    </div>
  )
}

function VirtualList({ listId, items }: { listId: ListId; items: string[] }) {
  return (
    <section className={styles.list}>
      <div className={styles.listTitle}>
        <span>{listId}</span>
        <span data-testid={`count-${listId}`}>{items.length} items</span>
      </div>
      <Droppable
        droppableId={listId}
        mode="virtual"
        renderClone={(provided, snapshot, rubric) => (
          <Item provided={provided} label={items[rubric.source.index] ?? ''} isDragging={snapshot.isDragging} />
        )}
      >
        {(provided, snapshot) => (
          <Scroller
            listId={listId}
            droppableProvided={provided}
            items={items}
            itemCount={items.length + (snapshot.isUsingPlaceholder ? 1 : 0)}
          />
        )}
      </Droppable>
    </section>
  )
}

function Scroller({
  listId,
  droppableProvided,
  items,
  itemCount,
}: {
  listId: ListId
  droppableProvided: DroppableProvided
  items: string[]
  itemCount: number
}) {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null)
  const innerRef = useRef(droppableProvided.innerRef)
  innerRef.current = droppableProvided.innerRef

  const setRef = useCallback((element: HTMLDivElement | null) => {
    setScrollElement(element)
    innerRef.current(element)
  }, [])

  const virtualizer = useVirtualizer({
    count: itemCount,
    getScrollElement: () => scrollElement,
    estimateSize: () => CARD_SLOT_HEIGHT,
    overscan: 4,
  })

  const virtualItems = virtualizer.getVirtualItems()

  return (
    <div ref={setRef} className={styles.scroller} data-testid={`scroller-${listId}`} {...droppableProvided.droppableProps}>
      <div className={styles.sizer} style={{ height: `${virtualizer.getTotalSize()}px` }}>
        {virtualItems.map((virtualItem) => {
          const label = items[virtualItem.index]
          if (label === undefined) return null
          return (
            <Draggable key={label} draggableId={label} index={virtualItem.index}>
              {(provided, snapshot) => (
                <Item
                  provided={provided}
                  label={label}
                  isDragging={snapshot.isDragging}
                  style={{
                    position: 'absolute',
                    top: virtualItem.start,
                    left: 0,
                    width: '100%',
                    height: CARD_SLOT_HEIGHT,
                  }}
                />
              )}
            </Draggable>
          )
        })}
      </div>
    </div>
  )
}

function Item({
  provided,
  label,
  isDragging,
  style,
}: {
  provided: DraggableProvided
  label: string
  isDragging: boolean
  style?: React.CSSProperties
}) {
  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      style={{ ...style, ...provided.draggableProps.style }}
      className={styles.item}
      data-testid="prototype-item"
    >
      <div className={`${styles.itemInner} ${isDragging ? styles.dragging : ''}`}>{label}</div>
    </div>
  )
}
